import type { ReactNode } from 'react';

import { Badge } from '@/designSystem/atoms/Badge';

export interface SelectableCardProps {
  title: string;
  description?: string;
  meta?: ReactNode;
  selected?: boolean;
  recommended?: boolean;
  badge?: string;
  onSelect?: () => void;
  'data-testid'?: string;
  children?: ReactNode;
}

/**
 * Large selectable row/card (reference: model picker — Gemini, Claude, GPT).
 */
export function SelectableCard({
  title,
  description,
  meta,
  selected = false,
  recommended = false,
  badge,
  onSelect,
  'data-testid': dataTestId,
  children,
}: SelectableCardProps) {
  return (
    <button
      type="button"
      data-testid={dataTestId ?? 'design-system-selectable-card'}
      onClick={onSelect}
      className={`ds-selectable-card w-full text-left ${selected ? 'ds-selectable-card-selected' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-primary">{title}</span>
            {recommended ? (
              <Badge variant="success" data-testid={`${dataTestId ?? 'design-system-selectable-card'}-recommended`}>
                Recommended
              </Badge>
            ) : null}
            {badge ? <Badge variant="info">{badge}</Badge> : null}
          </div>
          {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
          {meta ? <div className="mt-2 font-mono text-xs text-secondary">{meta}</div> : null}
          {children}
        </div>
        <span
          aria-hidden
          className={`mt-1 h-5 w-5 shrink-0 rounded-full border-2 ${
            selected ? 'border-success bg-success shadow-glow-success' : 'border-strong bg-surface'
          }`}
        />
      </div>
    </button>
  );
}
